/**
 * AdminFiles — Drive-like file browser for admin
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { SkeletonBar, Button } from '../components/ui';

import { T } from '../styles/tokens';
interface AdminFile {
  id: string;
  name: string;
  description: string;
  path: string;
  type: string;
  size: number | null;
  createdAt: string;
  category: string;
  icon: string;
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(d: string): string {
  try {
    const date = new Date(d);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
}

function categoryIcon(icon: string): string {
  const map: Record<string, string> = { chart: '◆', doc: '◇', code: '⬡', research: '◈' };
  return map[icon] || '◆';
}

/* Simple markdown to HTML */
function renderMarkdown(md: string): string {
  let html = md
    // Escape HTML
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Horizontal rules
    .replace(/^---+$/gm, '<hr/>')
    // Headers
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold + italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Blockquotes
    .replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    // Checkboxes
 .replace(/^- \[x\] (.+)$/gm, '<div class="md-check done"> $1</div>')
 .replace(/^- \[ \] (.+)$/gm, '<div class="md-check"> $1</div>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Tables
  html = html.replace(/(\|.+\|\n)+/g, (tableBlock) => {
    const lines = tableBlock.trim().split('\n');
    if (lines.length < 2) return tableBlock;
    const headerCells = lines[0]!.split('|').filter(c => c.trim());
    // Skip separator line
    const startIdx = lines[1]!.match(/^[\s|:-]+$/) ? 2 : 1;
    let table = '<div class="md-table-wrap"><table><thead><tr>';
    headerCells.forEach(c => { table += `<th>${c.trim()}</th>`; });
    table += '</tr></thead><tbody>';
    for (let i = startIdx; i < lines.length; i++) {
      const cells = lines[i]!.split('|').filter(c => c.trim());
      if (cells.length === 0) continue;
      table += '<tr>';
      cells.forEach(c => { table += `<td>${c.trim()}</td>`; });
      table += '</tr>';
    }
    table += '</tbody></table></div>';
    return table;
  });

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.+<\/li>\n?)+)/g, '<ul>$1</ul>');

  // Paragraphs (lines that aren't already wrapped)
  html = html.replace(/^(?!<[hublodta]|<hr|<div|<blockquote)(.+)$/gm, '<p>$1</p>');

  return html;
}

/* Icons */
const FolderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
  </svg>
);

const FileIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
  </svg>
);

const ListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);

export function AdminFiles() {
  if (!isLoggedIn.value) return <LoginPage />;

  const [files, setFiles] = useState<AdminFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [viewerFile, setViewerFile] = useState<AdminFile | null>(null);
  const [viewerContent, setViewerContent] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadFiles(); }, []);

  async function loadFiles() {
    try {
      const res = await api.get<{ files: AdminFile[] }>('/admin/files');
      setFiles(res.files || []);
    } catch (e) {
      console.error('[AdminFiles] Load error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function openFile(file: AdminFile) {
    setViewerFile(file);
    setViewerLoading(true);
    setViewerContent('');
    try {
      const res = await api.get<{ content: string }>(`/admin/files/${file.id}/content`);
      setViewerContent(res.content || '');
    } catch (e) {
      setViewerContent('Erro ao carregar conteúdo.');
    } finally {
      setViewerLoading(false);
    }
  }

  function downloadFile() {
    if (!viewerContent || !viewerFile) return;
    const blob = new Blob([viewerContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = viewerFile.path.split('/').pop() || 'file.md';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function copyContent() {
    if (!viewerContent) return;
    try {
      await navigator.clipboard.writeText(viewerContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  }

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: T.textMuted }}><FolderIcon /></span>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0 }}>Arquivos</h1>
          </div>
          <p style={{ color: T.textMuted, fontSize: 13, margin: '4px 0 0 30px' }}>
            {files.length} arquivo{files.length !== 1 ? 's' : ''} disponíve{files.length !== 1 ? 'is' : 'l'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <Button variant="ghost"
            onClick={() => setViewMode('grid')}
            style={{
              padding: '6px 10px', borderRadius: '8px 0 0 8px', border: `1px solid ${T.borderHover}`,
              background: viewMode === 'grid' ? T.primaryBg : T.bgCard,
              color: viewMode === 'grid' ? T.primary : T.textMuted, cursor: 'pointer',
              display: 'flex', alignItems: 'center',
            }}
          ><GridIcon /></Button>
          <Button variant="ghost"
            onClick={() => setViewMode('list')}
            style={{
              padding: '6px 10px', borderRadius: '0 8px 8px 0', border: `1px solid ${T.borderHover}`,
              borderLeft: 'none',
              background: viewMode === 'list' ? T.primaryBg : T.bgCard,
              color: viewMode === 'list' ? T.primary : T.textMuted, cursor: 'pointer',
              display: 'flex', alignItems: 'center',
            }}
          ><ListIcon /></Button>
        </div>
      </div>

      {/* Empty state */}
      {files.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMuted }}>
          <span style={{ fontSize: 40, opacity: 0.3 }}><FolderIcon /></span>
          <div style={{ marginTop: 12 }}>Nenhum arquivo disponível</div>
        </div>
      )}

      {/* Grid view */}
      {viewMode === 'grid' && files.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {files.map(f => (
            <div
              key={f.id}
              onClick={() => openFile(f)}
              style={{
                padding: 20, background: T.bgCard, borderRadius: 14,
                border: `1px solid ${T.bgHover}`, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.bgHover; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: T.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: T.primary, flexShrink: 0,
                }}>
                  <FileIcon />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4, lineHeight: 1.3 }}>{f.name}</div>
                  <div style={{
                    fontSize: 12, color: T.textMuted, lineHeight: 1.4,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any,
                    overflow: 'hidden',
                  }}>{f.description}</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                  background: T.primaryBg, color: T.primary,
                }}>{f.category}</span>
                <div style={{ display: 'flex', gap: 12, fontSize: 11, color: T.textDisabled }}>
                  {f.size && <span>{formatSize(f.size)}</span>}
                  <span>{formatDate(f.createdAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List view */}
      {viewMode === 'list' && files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {files.map(f => (
            <div
              key={f.id}
              onClick={() => openFile(f)}
              style={{
                padding: '14px 18px', background: T.bgCard, borderRadius: 10,
                border: `1px solid ${T.bgHover}`, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.15s',
              }}
              onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.bgHover; }}
            >
              <div style={{ color: T.primary, flexShrink: 0 }}><FileIcon /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{f.name}</div>
                <div style={{ fontSize: 12, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.description}</div>
              </div>
              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.primaryBg, color: T.primary, flexShrink: 0 }}>{f.category}</span>
              <span style={{ fontSize: 11, color: T.textDisabled, flexShrink: 0 }}>{formatDate(f.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {/* File Viewer Modal */}
      {viewerFile && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)',
            display: 'flex', justifyContent: 'center', alignItems: 'stretch',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) { setViewerFile(null); setViewerContent(''); } }}
        >
          <div style={{
            width: '100%', maxWidth: 900, margin: '20px', background: T.bg,
            borderRadius: 16, border: `1px solid ${T.borderHover}`,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Viewer Header */}
            <div style={{
              padding: '16px 20px', borderBottom: `1px solid ${T.bgHover}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{viewerFile.name}</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{viewerFile.path}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <Button variant="ghost"
                  onClick={copyContent}
                  style={{
                    padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.borderHover}`,
                    background: copied ? T.successBg : T.bgCard,
                    color: copied ? T.success : T.textSecondary, cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <CopyIcon /> {copied ? 'Copiado!' : 'Copiar'}
                </Button>
                <Button variant="ghost"
                  onClick={downloadFile}
                  style={{
                    padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.primary}`,
                    background: T.primaryBg, color: T.primary, cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <DownloadIcon /> Download
                </Button>
                <Button variant="ghost"
                  onClick={() => { setViewerFile(null); setViewerContent(''); }}
                  style={{
                    padding: 6, borderRadius: 8, border: 'none',
                    background: 'transparent', color: T.textMuted, cursor: 'pointer',
                  }}
                >
                  <CloseIcon />
                </Button>
              </div>
            </div>

            {/* Viewer Content */}
            <div ref={contentRef} style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
              {viewerLoading ? (
                <div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>
              ) : (
                <div
                  class="md-content"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(viewerContent) }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Markdown styles */}
      <style>{`
        .md-content { color: #C9D1D9; font-size: 14px; line-height: 1.7; }
        .md-content h1 { font-size: 22px; font-weight: 800; color: #E8EEF9; margin: 28px 0 12px; padding-bottom: 8px; border-bottom: 1px solid var(--t-white-alpha-8); }
        .md-content h2 { font-size: 18px; font-weight: 700; color: #E8EEF9; margin: 24px 0 10px; }
        .md-content h3 { font-size: 15px; font-weight: 700; color: #D1D9E3; margin: 20px 0 8px; }
        .md-content h4 { font-size: 14px; font-weight: 600; color: ${T.textSecondary}; margin: 16px 0 6px; }
        .md-content p { margin: 8px 0; }
        .md-content strong { color: #E8EEF9; }
        .md-content code { background: ${T.primaryBgSubtle}; color: #79C0FF; padding: 2px 6px; borderRadius: 4px; font-size: 13px; font-family: 'SF Mono', monospace; }
        .md-content hr { border: none; border-top: 1px solid var(--t-white-alpha-8); margin: 20px 0; }
        .md-content blockquote { border-left: 3px solid ${T.primaryBgStrong}; padding: 4px 16px; margin: 12px 0; color: #8B949E; }
        .md-content ul { padding-left: 20px; margin: 8px 0; }
        .md-content li { margin: 3px 0; }
        .md-content a { color: #58A6FF; text-decoration: none; }
        .md-content a:hover { text-decoration: underline; }
        .md-table-wrap { overflow-x: auto; margin: 12px 0; }
        .md-content table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .md-content th { background: ${T.borderSubtle}; padding: 8px 12px; text-align: left; font-weight: 600; color: ${T.textSecondary}; border: 1px solid ${T.border}; }
        .md-content td { padding: 6px 12px; border: 1px solid ${T.border}; color: #8B949E; }
        .md-content tr:hover td { background: var(--t-white-alpha-2); }
        .md-check { padding: 2px 0; color: #8B949E; }
        .md-check.done { color: #6EE7B7; }
      `}</style>
    </div>
  );
}
